import { useRefs } from 'contexts/RefContext';
import Konva from 'konva';
import { useEffect, useMemo, useRef } from 'react';
import { Circle, Group, Line } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { setDeletedObstructionIDs, setObstructionEditPointAction, setSelectedObstructionId } from 'store/slices/ToolSlice';
import { OBS_EDIT_POINT_ACTION } from 'store/slices/ToolSlice/types';
import { KonvaGroupNames } from './tool/utils';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { pushUndoAction } from 'store/slices/ObstructionUndoSlice';
import { EditableRawObstruction } from './EditableRawObstruction';
import { detectShape, getObstructionHandleSize, OBSTRUCTION_DEFAULT_FILL, simplifyCoordsByDistance } from './tool/Editor/AddObstructionBtn/utils';
import { appToast } from 'components/Basic/CustomToast';
import InvalidFacetEditToast from 'components/tool/Group/InvalidModal';

const SIMPLIFICATION_DIST = 7.5;

const calculateCentroid = (points: number[]) => {
	let sumX = 0, sumY = 0;
	const numPoints = points.length / 2;

	for (let i = 0; i < points.length; i += 2) {
		sumX += points[i];
		sumY += points[i + 1];
	}

	return {
		x: sumX / numPoints,
		y: sumY / numPoints,
	};
};

function getCircleFromPoints(points: number[]) {
	// centroid
	let sumX = 0, sumY = 0;
	const n = points.length / 2;
	for (let i = 0; i < points.length; i += 2) {
		sumX += points[i];
		sumY += points[i + 1];
	}
	const centerX = sumX / n;
	const centerY = sumY / n;

	// radius = average distance to centroid
	let sumR = 0;
	for (let i = 0; i < points.length; i += 2) {
		const dx = points[i] - centerX;
		const dy = points[i + 1] - centerY;
		sumR += Math.sqrt(dx * dx + dy * dy);
	}
	const radius = sumR / n;

	return { x: centerX, y: centerY, radius };
}

export default function Obstructions() {
	const isTouch = typeof window !== 'undefined' && (
		'ontouchstart' in window || navigator.maxTouchPoints > 0
	);
	const scale = useKonvaImageScale();
	const dispatch = useDispatch();
	const { konvaRef, isDrawingRef: isDrawing } = useRefs();

	const { obstructions } = useSelector((state: RootState) => state.roofData.data);
	const { activePanelMode } = useSelector((state: RootState) => state.panelSlice.data);
	const { deletedObstructionIDs, moveStage, rotateStage, editModeEnabled, enableEditObs,
		deleteObstructionsMode, editedObstructions, selectedObstructionId, obstructionEditPointAction, zoomLevel, fullScreenEnabled, invalidObstructionIds } = useSelector((state: RootState) => state.toolNewPostions.data);
	const manualObs = useSelector((state: RootState) => state.toolNewPostions.data.manualObs);
	const isIrradianceOn = useSelector((state: RootState) => state.Irradiance.data.showIrradiance);
	const shuldListenClickEvents = !moveStage && !rotateStage && deleteObstructionsMode && !activePanelMode;
	const isEditObsMode = enableEditObs && !deleteObstructionsMode;
	const isDeletePointMode = isEditObsMode && obstructionEditPointAction === OBS_EDIT_POINT_ACTION.DELETE_POINT;
	const prevSelectedObsRef = useRef<string | null>(null);
	const pointsHighlightCacheRef = useRef<Record<string, { highlighted: Set<string>; removed: Set<string> }>>({});
	const handleZoomMode = fullScreenEnabled ? 'fullScreen' : 'windowed';

	const obstructionsList = useMemo(() => (obstructions.map((obs) => {
		const edited = editedObstructions?.[obs.id];
		const obstruction = edited
			? {
				...obs,
				coordinates: edited.coordinates,
				assessment: edited.assessment ?? obs.assessment,
				area: edited.area,
				userMapped: edited.userMapped,
			}
			: obs;
		let cirlceConfig: Konva.CircleConfig | undefined = undefined;
		

		if (obstruction.assessment === 'RAW_LARGE') {
			const centroid = calculateCentroid(obstruction.coordinates.flat());
			const radius = getObstructionHandleSize(10, zoomLevel, handleZoomMode);
			cirlceConfig = {
				radius,
				x: centroid.x,
				y: centroid.y,
				fill: '#FF0000',
				stroke: '#FF26006B',
				strokeWidth: 2,

			};
		}
		else if (obstruction.assessment === 'CIRCLE') {
			cirlceConfig = {
				...getCircleFromPoints(obstruction.coordinates.flat()),
				fill: OBSTRUCTION_DEFAULT_FILL,
				stroke: 'red',
				strokeWidth: 1,
			};
		}

		return { ...obstruction, cirlceConfig };
	})), [editedObstructions, handleZoomMode, obstructions, zoomLevel]);

	const selectedObstructionIsPolygon = useMemo(() => {
		if (!selectedObstructionId) return false;
		const obs = obstructionsList.find((o) => o.id === selectedObstructionId);
		if (obs) {
			if (obs.assessment === 'CIRCLE') return false;
			return detectShape(obs.coordinates.flat()) === 'POLYGON';
		}
		const manual = manualObs[selectedObstructionId];
		if (!manual) return false;
		if (manual.shapeType === 'CIRCLE') return false;
		return detectShape(manual.coordinates.flat()) === 'POLYGON';
	}, [manualObs, obstructionsList, selectedObstructionId]);

	useEffect(() => {
		if (!enableEditObs) {
			pointsHighlightCacheRef.current = {};
		}
	}, [enableEditObs]);

	useEffect(() => {
		if (!enableEditObs || deleteObstructionsMode) {
			if (appToast.isActive('invalid-obstruction-error')) {
				appToast.dismiss({
					id: 'invalid-obstruction-error',
					containerId: 'tool-toast'
				});
			}
			return;
		}

		if (invalidObstructionIds.length > 0) {
			if (appToast.isActive('invalid-obstruction-error')) return;
			appToast.sticky(
				<InvalidFacetEditToast shouldDisable={false} />,
				{
					toastId: 'invalid-obstruction-error',
					className: 'invalid-error-toast',
					containerId: 'tool-toast'
				}
			);
		} else if (appToast.isActive('invalid-obstruction-error')) {
			appToast.dismiss({
				id: 'invalid-obstruction-error',
				containerId: 'tool-toast'
			});
		}
	}, [deleteObstructionsMode, enableEditObs, invalidObstructionIds.length]);

	useEffect(() => {
		if (!isEditObsMode) {
			dispatch(setObstructionEditPointAction({ actionType: null }));
			prevSelectedObsRef.current = selectedObstructionId;
			return;
		}

		if (!selectedObstructionId || !selectedObstructionIsPolygon) {
			dispatch(setObstructionEditPointAction({ actionType: null }));
			prevSelectedObsRef.current = selectedObstructionId;
			return;
		}

		if (selectedObstructionId !== prevSelectedObsRef.current) {
			prevSelectedObsRef.current = selectedObstructionId;
			dispatch(setObstructionEditPointAction({ actionType: OBS_EDIT_POINT_ACTION.ADD_POINT }));
		}
	}, [dispatch, isEditObsMode, selectedObstructionId, selectedObstructionIsPolygon]);

	function handleClick(evt: Konva.KonvaEventObject<MouseEvent | Event>) {
		if (isDrawing.current) return;
		if (deleteObstructionsMode) {
			dispatch(setDeletedObstructionIDs({ obstructionID: evt.target.id() }));
			dispatch(pushUndoAction({
				type: 'DELETE',
				obstructionId: evt.target.id(),
				wasInProgress: false,
			}));

			if (konvaRef?.current)
				konvaRef.current.container().style.cursor = 'default';
			return;
		}
		if (isEditObsMode) {
			evt.cancelBubble = true;
			const clickedId = evt.target.id() || evt.target.getParent()?.id();
			if (!clickedId) return;
			if (selectedObstructionId !== clickedId) {
				dispatch(setSelectedObstructionId({ id: clickedId }));
			}
		}
	}

	function handleHover(hover: 'ENTER' | 'LEAVE') {
		if (!deleteObstructionsMode || isDeletePointMode) return;
		if (konvaRef?.current) {
			konvaRef.current.container().style.cursor = hover === 'ENTER' ? 'pointer' : 'default';
		}
	}

	return (
		<Group
			name={KonvaGroupNames.obstructionsGroup}
			visible={editModeEnabled && !isIrradianceOn}
			listening={shuldListenClickEvents || isEditObsMode}
			scale={scale}
		>
			{
				obstructionsList.map((obstruction) => {
					if (deletedObstructionIDs.includes(obstruction.id)) return null;
					const isAlreadyEdited = !!editedObstructions?.[obstruction.id];
					// Determine if we should simplify points based on your existing logic
					const shouldSimplify = !isAlreadyEdited && (obstruction.assessment === 'RAW' || obstruction.assessment === 'RAW_LARGE')
						&& !obstruction.userMapped;
					const displayPoints = shouldSimplify
						? simplifyCoordsByDistance(obstruction.coordinates.flat(), SIMPLIFICATION_DIST)
						: obstruction.coordinates.flat();
					// ------- OBSTRUCTION_ASSESSMENT : RAW_LARGE(AUTO) ---------
					if (obstruction.cirlceConfig && obstruction.assessment === 'RAW_LARGE') {
						return isEditObsMode ? (
							<EditableRawObstruction
								key={obstruction.id}
								id={obstruction.id}
								points={!isAlreadyEdited ? simplifyCoordsByDistance(obstruction.coordinates.flat(), SIMPLIFICATION_DIST)
									: obstruction.coordinates.flat()
								}
								assessment={obstruction.assessment}
								userMapped
								readOnly={false}
								interactive={selectedObstructionId === obstruction.id}
								pointsHighlightCacheRef={pointsHighlightCacheRef}
								onClick={handleClick}
								onTap={handleClick}
								onMouseEnter={() => handleHover('ENTER')}
								onMouseLeave={() => handleHover('LEAVE')}
							/>
						) : (
							<Circle
								{...obstruction.cirlceConfig}
								id={obstruction.id}
								key={obstruction.id}
								strokeScaleEnabled={false}
								closed={true}
								hitStrokeWidth={isTouch ? 10 : undefined}
								hitFunc={(ctx, shape) => {
									const radius = obstruction.cirlceConfig?.radius ?? 1;
									const hitRadius = (isTouch ? 10 : Math.max(radius, 2));
									ctx.beginPath();
									ctx.arc(0, 0, hitRadius, 0, Math.PI * 2);
									ctx.closePath();
									ctx.fillStrokeShape(shape);
								}}
								onClick={handleClick}
								onTap={handleClick}
								onMouseEnter={() => handleHover('ENTER')}
								onMouseLeave={() => handleHover('LEAVE')}

							/>
						);
					}
					// ------- OBSTRUCTION_ASSESSMENT : RAW/CIRCLE (EDITABLE) ---------
					if ((obstruction.userMapped) || obstruction.cirlceConfig || obstruction.assessment == 'RAW') {
						return isEditObsMode ? (
							<EditableRawObstruction
								key={obstruction.id}
								id={obstruction.id}
								points={displayPoints}
								assessment={obstruction.assessment}
								userMapped
								readOnly={false}
								interactive={selectedObstructionId === obstruction.id}
								pointsHighlightCacheRef={pointsHighlightCacheRef}
								onClick={handleClick}
								onTap={handleClick}
								onMouseEnter={() => handleHover('ENTER')}
								onMouseLeave={() => handleHover('LEAVE')}
							/>
						) : obstruction.cirlceConfig ? (
							<Circle
								{...obstruction.cirlceConfig}
								id={obstruction.id}
								key={obstruction.id}
								strokeScaleEnabled={false}
								hitStrokeWidth={isTouch ? 10 : undefined}
								onClick={handleClick}
								onTap={handleClick}
								onMouseEnter={() => handleHover('ENTER')}
								onMouseLeave={() => handleHover('LEAVE')}
							/>
						)
							: (
								<Line
									id={obstruction.id}
									key={obstruction.id}
									points={displayPoints}
									strokeWidth={1}
									strokeScaleEnabled={false}
									stroke="red"
									fill={OBSTRUCTION_DEFAULT_FILL}
									closed
									hitStrokeWidth={isTouch ? 10 : undefined}
									onClick={handleClick}
									onTap={handleClick}
									onMouseEnter={() => handleHover('ENTER')}
									onMouseLeave={() => handleHover('LEAVE')}
								/>
							);
					}
				})
			}
		</Group>
	);
}
